// Disable sorting objects for this file as the order of certain objects (like conditionalRenderMap) is significant for the application logic
/* eslint-disable perfectionist/sort-objects */
import type { FunctionComponent, ReactNode } from "react";
import { useMemo } from "react";
import { RelayEnvironmentProvider } from "react-relay";

import { RosettaCampaignPopup } from "scmp-app/components/popup-container/rosetta-campaign-popup";
import { useRosettaCampaignPopup } from "scmp-app/components/popup-container/rosetta-campaign-popup/hooks";
import { createClientEnvironmentWithCred } from "scmp-app/lib/relay/environment.client";

import { AcquisitionPopupContainerWrapper } from "./acquisition-popup-container-wrapper";
import { useAcquisitionPopupContainerWrapper } from "./acquisition-popup-container-wrapper/hooks";
import { Onboarding } from "./onboarding";
import { useOnboarding } from "./onboarding/hooks";
import { SignUpActionRedirect } from "./sign-up-action-redirect";
import { useNewsletterSignUpRedirect } from "./sign-up-action-redirect/hooks";
import { SubscriberOnboarding } from "./subscriber-onboarding";
import { useSubscriberOnboarding } from "./subscriber-onboarding/hooks";

export const PopupContainer: FunctionComponent = () => {
  const environmentWithCredentials = createClientEnvironmentWithCred();

  const { showPopup: shouldShowNewsletterSignUpRedirect } = useNewsletterSignUpRedirect();
  const { shouldShowOnboarding } = useOnboarding();
  const { shouldShowSubscriberOnboarding } = useSubscriberOnboarding();
  const { shouldShowAcquisitionPopup } = useAcquisitionPopupContainerWrapper();
  const { shouldShowRosettaCampaignPopup } = useRosettaCampaignPopup();

  const conditionalRenderMap: Record<string, { component: ReactNode; needToShow: boolean }> =
    useMemo(
      () => ({
        [SignUpActionRedirect.name]: {
          component: <SignUpActionRedirect />,
          needToShow: shouldShowNewsletterSignUpRedirect,
        },
        [SubscriberOnboarding.name]: {
          component: <SubscriberOnboarding />,
          needToShow: shouldShowSubscriberOnboarding,
        },
        [Onboarding.name]: {
          component: (
            <RelayEnvironmentProvider environment={environmentWithCredentials}>
              {/*
                  TODO: This implementation will potentially render popup from action behind onboarding
                  it's for ensure the popup success event will be sent
                */}
              <AcquisitionPopupContainerWrapper skipTriggerFromConfig />
              <Onboarding />
            </RelayEnvironmentProvider>
          ),
          needToShow: shouldShowOnboarding,
        },
        [RosettaCampaignPopup.name]: {
          component: <RosettaCampaignPopup />,
          needToShow: shouldShowRosettaCampaignPopup,
        },
        [AcquisitionPopupContainerWrapper.name]: {
          component: <AcquisitionPopupContainerWrapper />,
          needToShow: shouldShowAcquisitionPopup,
        },
      }),
      [
        environmentWithCredentials,
        shouldShowAcquisitionPopup,
        shouldShowNewsletterSignUpRedirect,
        shouldShowOnboarding,
        shouldShowRosettaCampaignPopup,
        shouldShowSubscriberOnboarding,
      ],
    );

  const Component = useMemo(
    () =>
      Object.values(conditionalRenderMap).find(componentItem => componentItem.needToShow)
        ?.component ?? null,
    [conditionalRenderMap],
  );

  return Component;
};

PopupContainer.displayName = "PopupContainer";
